import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Search, Calendar, Crown, Shield, Clock, RotateCcw, XCircle, Plus } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

export const UserManagement = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, action: null, user: null });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/admin/users`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch users');

      const data = await response.json();
      setUsers(data.users || []);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleTrialAction = async (userId, action, days = null) => {
    setProcessing(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/admin/users/${userId}/trial`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ action, days })
      });

      if (!response.ok) throw new Error('Failed to update trial');

      const data = await response.json();
      
      // Update local user data
      setUsers(users.map(u => u.user_id === userId ? data.user : u));
      
      toast.success(data.message);
      setActionDialog({ open: false, action: null, user: null });
      
    } catch (error) {
      console.error('Trial action error:', error);
      toast.error('Failed to update trial');
    } finally {
      setProcessing(false);
    }
  };

  const openActionDialog = (action, user) => {
    setActionDialog({ open: true, action, user });
  };

  const getTierColor = (tier) => {
    const colors = {
      'free': 'bg-slate-100 text-slate-800',
      'premium': 'bg-blue-100 text-blue-800',
      'family': 'bg-purple-100 text-purple-800',
      'fleet': 'bg-orange-100 text-orange-800'
    };
    return colors[tier] || 'bg-gray-100 text-gray-800';
  };

  const getTrialStatus = (user) => {
    if (!user.subscription?.trial_end_date) return null;
    
    const endDate = new Date(user.subscription.trial_end_date);
    const now = new Date();
    const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysLeft <= 0) return { status: 'expired', daysLeft: 0, color: 'bg-red-100 text-red-800' };
    if (daysLeft <= 3) return { status: 'expiring', daysLeft, color: 'bg-orange-100 text-orange-800' };
    return { status: 'active', daysLeft, color: 'bg-green-100 text-green-800' };
  };

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">User Management</h1>
        <p className="text-muted-foreground mt-1">
          Manage user subscriptions and trial periods
        </p>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by email or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button onClick={fetchUsers} variant="outline">
              <RotateCcw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>Users ({filteredUsers.length})</CardTitle>
          <CardDescription>
            Manage trial periods and subscription status
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Trial Status</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => {
                    const trialStatus = getTrialStatus(user);
                    
                    return (
                      <TableRow key={user.user_id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{user.name}</div>
                            <div className="text-sm text-muted-foreground">{user.email}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getTierColor(user.subscription?.tier || 'free')}>
                            {(user.subscription?.tier || 'free').toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {trialStatus ? (
                            <div className="flex items-center gap-2">
                              <Badge className={trialStatus.color}>
                                {trialStatus.daysLeft > 0 
                                  ? `${trialStatus.daysLeft} days left`
                                  : 'Expired'
                                }
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {new Date(user.subscription.trial_end_date).toLocaleDateString()}
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">No trial</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={user.subscription?.status === 'active' ? 'default' : 'secondary'}>
                            {user.subscription?.status || 'inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openActionDialog('reset', user)}
                              data-testid={`reset-trial-${user.user_id}`}
                            >
                              <RotateCcw className="h-3 w-3 mr-1" />
                              Reset
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openActionDialog('extend', user)}
                              data-testid={`extend-trial-${user.user_id}`}
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Extend
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openActionDialog('revoke', user)}
                              data-testid={`revoke-trial-${user.user_id}`}
                            >
                              <XCircle className="h-3 w-3 mr-1" />
                              End
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => setActionDialog({ ...actionDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionDialog.action === 'reset' && 'Reset Trial Period'}
              {actionDialog.action === 'extend' && 'Extend Trial Period'}
              {actionDialog.action === 'revoke' && 'End Trial Period'}
            </DialogTitle>
            <DialogDescription>
              {actionDialog.action === 'reset' && 
                'This will restart the trial period for 30 days from today.'}
              {actionDialog.action === 'extend' && 
                'This will add 7 more days to the current trial period.'}
              {actionDialog.action === 'revoke' && 
                'This will immediately end the trial period and downgrade to free tier.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="text-sm">
              <strong>User:</strong> {actionDialog.user?.name} ({actionDialog.user?.email})
            </div>
            <div className="text-sm mt-2">
              <strong>Current Tier:</strong> {actionDialog.user?.subscription?.tier?.toUpperCase() || 'FREE'}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setActionDialog({ open: false, action: null, user: null })}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                const days = actionDialog.action === 'reset' ? 30 : actionDialog.action === 'extend' ? 7 : null;
                handleTrialAction(actionDialog.user?.user_id, actionDialog.action, days);
              }}
              disabled={processing}
              variant={actionDialog.action === 'revoke' ? 'destructive' : 'default'}
            >
              {processing ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
